import { Filter, GlProgram } from "pixi.js";
import { FILTER_VERTEX_GLSL } from "./filterVertex";

export type ColorOverlayBlendMode = "darken" | "softLight";

export function colorOverlayFragmentGlsl(mode: ColorOverlayBlendMode): string {
  const blend =
    mode === "softLight"
      ? `vec3 blended = vec3(softLightChannel(straight.r, uOverlayColor.r), softLightChannel(straight.g, uOverlayColor.g), softLightChannel(straight.b, uOverlayColor.b));`
      : `vec3 blended = min(straight, uOverlayColor.rgb);`;
  return `
in vec2 vTextureCoord;
out vec4 finalColor;
uniform sampler2D uTexture;
uniform vec4 uOverlayColor; // rgb = tint
uniform vec4 uParams;       // x = strength (0..1), y = tintStrength (luminance colourise), zw unused

float softLightChannel(float base, float blend) {
  if (blend <= 0.5) {
    return base - (1.0 - 2.0 * blend) * base * (1.0 - base);
  }
  float g = (base <= 0.25) ? ((16.0 * base - 12.0) * base + 4.0) * base : sqrt(base);
  return base + (2.0 * blend - 1.0) * (g - base);
}

void main(void) {
  vec4 o = texture(uTexture, vTextureCoord);
  float strength = uParams.x;
  // Pixi filter input is PREMULTIPLIED: un-premultiply → blend → re-premultiply,
  // exactly like the Spine variant of the effect (edge-correct at any alpha).
  if (strength > 0.0 && o.a > 0.001) {
    vec3 straight = o.rgb / o.a;
    ${blend}
    straight = mix(straight, blended, strength);
    float lum = dot(straight, vec3(0.299, 0.587, 0.114));
    straight = mix(straight, uOverlayColor.rgb * lum, strength * uParams.y);
    o.rgb = straight * o.a;
  }
  finalColor = o;
}
`;
}

export interface ColorOverlayOptions {
  mode?: ColorOverlayBlendMode;
  /** 0..1 rgb tint, default red. */
  color?: [number, number, number];
  strength?: number;
  /** Extra luminance-preserving colourise (the Spine variant's `tintStrength`). Default 0. */
  tintStrength?: number;
}

/**
 * Port of `SpriteColorOverlay.effect` + `SpineColorOverlay.effect` in one:
 * Photoshop-style colour overlay, `darken` (per-channel min) or `softLight`,
 * faded in by `strength`; optional `tintStrength` colourises bright areas
 * toward the tint while preserving brightness (used on the anticipation
 * background ramp). Works on sprites and Spine alike (both premultiplied here).
 */
export class ColorOverlayFilter extends Filter {
  public constructor(options: ColorOverlayOptions = {}) {
    const mode = options.mode ?? "darken";
    const color = options.color ?? [1, 0, 0];
    super({
      glProgram: GlProgram.from({ vertex: FILTER_VERTEX_GLSL, fragment: colorOverlayFragmentGlsl(mode), name: `slot-color-overlay-${mode}`, preferredFragmentPrecision: "highp" }),
      resources: {
        overlayUniforms: {
          uOverlayColor: { value: [color[0], color[1], color[2], 1], type: "vec4<f32>" },
          uParams: { value: [options.strength ?? 1, options.tintStrength ?? 0, 0, 0], type: "vec4<f32>" },
        },
      },
    });
  }

  private get params(): Float32Array {
    return this.resources.overlayUniforms.uniforms.uParams as Float32Array;
  }

  public get strength(): number {
    return this.params[0]!;
  }
  public set strength(v: number) {
    this.params[0] = v;
  }
  public set tintStrength(v: number) {
    this.params[1] = v;
  }
  public setColor(r: number, g: number, b: number): void {
    const c = this.resources.overlayUniforms.uniforms.uOverlayColor as Float32Array;
    c[0] = r;
    c[1] = g;
    c[2] = b;
  }
}
